import NiceModal from '@ebay/nice-modal-react';

import { useMemo } from 'react';
import { useFieldArray } from 'react-hook-form';

import AutoComplete from 'components/field/AutoComplete';
import IntegerInput from 'components/field/IntegerInput';
import FormItem from 'components/form/FormItem';
import NoRecordsFound from 'components/global/NoRecordsFound';
import Typography from 'components/global/Typography';
import Table from 'components/table/Table';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';

import ProductForm from 'pages/Products/Products/ProductForm';

import { AUTOCOMPLETE, ORDER_ACTIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';

export const PurchaseOrderItems = ({ formMethods, formMode, isLoading, orderID }) => {
	const isMobile = useMediaQuery(Breakpoints.mobile);
	const isAuthorized = useAccessControl();
	const axios = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();
	const { Currency } = useLocale();

	// Form methods passed from inbound form
	const { control, errors, watch, setValue, getValues } = formMethods;

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'purchaseOrderItems'
	});

	const productsWatcher = watch('purchaseOrderItems');

	const controlledFields = fields.map((field, index) => ({
		...field,
		...productsWatcher[index]
	}));

	const removeItem = async ({ index, original: { ID } }) => {
		if (ID) {
			try {
				const response = await axios.post(ORDER_ACTIONS.DELETE_ORDER_ITEM, {
					otoOrderID: orderID,
					ID
				});
				if (response.status === 200) {
					showSuccess();
					remove(index);
				} else {
					showError();
					AppLogger.error(response);
				}
			} catch (error) {
				AppLogger.error(error);
				showError();
			}
		} else {
			remove(index);
		}
	};

	const productAutocompleteLabels = useMemo(() => ['name'], []);
	const productAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'name', width: 200 },
			{ accessor: 'SKU', width: 200 }
		],
		[]
	);

	const columns = useMemo(
		() => [
			{
				id: 'purchaseOrderItems',
				header: 'product',
				required: true,
				size: isMobile ? 120 : 200,
				renderCell: ({ row }) => (
					<AutoComplete
						key={row.id}
						autoCompleteName={AUTOCOMPLETE.PRODUCTS}
						value={watch(`purchaseOrderItems[${row.index}].productID`)}
						columns={productAutocompleteColumns}
						name={`purchaseOrderItems[${row.index}].productID`}
						disabled={formMode === 'view'}
						selectionLabelAccessor={productAutocompleteLabels}
						setValue={(value, isFirstRender) => {
							if (isFirstRender) return;
							setValue(`purchaseOrderItems[${row.index}].productID`, value?.ID ?? null, {
								shouldValidate: true,
								shouldDirty: true
							});
							setValue(`purchaseOrderItems[${row.index}].name`, value?.name ?? null, {
								shouldValidate: true,
								shouldDirty: true
							});
							setValue(`purchaseOrderItems[${row.index}].SKU`, value?.SKU ?? null, {
								shouldValidate: true,
								shouldDirty: true
							});
							setValue(`purchaseOrderItems[${row.index}].price`, value?.price ?? null, {
								shouldValidate: true,
								shouldDirty: true
							});
							setValue(
								`purchaseOrderItems[${row.index}].lineTotal`,
								(value?.price ?? 0) * getValues(`purchaseOrderItems[${row.index}].qty`),
								{
									shouldValidate: true,
									shouldDirty: true
								}
							);
						}}
						firstItem={
							isAuthorized('/autocomplete/product/addANewProduct')
								? {
										action: () =>
											NiceModal.show(ProductForm, {
												setProduct: (value) => {
													setValue(`purchaseOrderItems[${row.index}].productID`, value, {
														shouldValidate: true,
														shouldDirty: true
													});
												}
											}),
										label: <Typography color="var(--blue)">addNewProduct</Typography>
									}
								: undefined
						}
					/>
				),
				showOnMobile: true
			},
			{
				header: 'sku',
				accessorKey: 'SKU',
				size: 60,
				maxSize: 60,
				showOnMobile: true
			},
			{
				header: 'quantity',
				accessorKey: 'qty',
				size: 60,
				maxSize: 60,
				renderCell: ({ row }) => (
					<IntegerInput
						errorMessage={errors?.purchaseOrderItems?.[row.index]?.qty?.message}
						showArrows={false}
						value={watch(`purchaseOrderItems[${row.index}].qty`)}
						setValue={(value) => {
							setValue(`purchaseOrderItems[${row.index}].qty`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
							setValue(
								`purchaseOrderItems[${row.index}].lineTotal`,
								value * (getValues(`purchaseOrderItems[${row.index}].price`) ?? 0),
								{
									shouldValidate: true,
									shouldDirty: true
								}
							);
						}}
						viewMode={formMode === 'view'}
					/>
				),
				showOnMobile: true
			},
			{
				header: 'price',
				accessorKey: 'price',
				size: 100,
				renderCell: ({ row }) => (
					<IntegerInput
						value={watch(`purchaseOrderItems[${row.index}].price`)}
						setValue={(value) => {
							setValue(`purchaseOrderItems[${row.index}].price`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
							setValue(
								`purchaseOrderItems[${row.index}].lineTotal`,
								getValues(`purchaseOrderItems[${row.index}].qty`) * (value ?? 0),
								{
									shouldValidate: true,
									shouldDirty: true
								}
							);
						}}
						unit={Currency}
						viewMode={formMode === 'view'}
						allowFloat
					/>
				)
			},
			{
				header: 'total',
				accessorKey: 'lineTotal',
				size: 100,
				renderCell: ({ row }) => (
					<IntegerInput
						name={`purchaseOrderItems[${row.index}].lineTotal`}
						unit={Currency}
						viewMode={formMode === 'view'}
						allowFloat
					/>
				)
			},
			...(formMode === 'edit' || formMode === 'add'
				? [
						{
							id: 'deleteRow',
							size: 30,
							maxSize: 30,
							showOnMobile: true
						}
					]
				: [])
		],
		[isMobile]
	);

	return (
		<FormItem>
			<Table
				columns={columns}
				data={controlledFields}
				loading={isLoading}
				noRecordsFound={
					formMode !== 'add' ? (
						<NoRecordsFound
							lottieName="no-items"
							height="10rem"
							title="noItems"
							subTitle="noItemsSubtitle"
						/>
					) : (
						<></>
					)
				}
				showAddNewRowButton={formMode !== 'view'}
				onRowDelete={removeItem}
				onRowAdd={() => {
					append({
						productID: null,
						qty: 1,
						price: null,
						lineTotal: null
					});
				}}
			/>
		</FormItem>
	);
};
